import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface OfflineRecord {
  id: string;
  table: string;
  action: 'insert' | 'update' | 'delete';
  data: any;
  timestamp: number;
  userId?: string;
}

const DB_NAME = 'itacondo_offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending_operations';

export const useOfflineSync = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Inicializar IndexedDB
  const initDB = useCallback((): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('table', 'table', { unique: false });
        }
      };
    });
  }, []);

  // Salvar operação offline
  const saveOfflineOperation = useCallback(async (
    table: string,
    action: 'insert' | 'update' | 'delete',
    data: any,
    userId?: string
  ) => {
    try {
      const db = await initDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const record: OfflineRecord = {
        id: `${table}_${action}_${Date.now()}_${Math.random()}`,
        table,
        action,
        data,
        timestamp: Date.now(),
        userId
      };
      
      await store.add(record);
      await getPendingCount();
      
      toast({
        title: "Operação salva offline",
        description: "Dados serão sincronizados quando a internet voltar",
        duration: 3000,
      });
      
    } catch (error) {
      console.error('Erro ao salvar operação offline:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar a operação offline",
        variant: "destructive",
      });
    }
  }, [initDB]);

  // Contar operações pendentes
  const getPendingCount = useCallback(async () => {
    try {
      const db = await initDB();
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      return new Promise<number>((resolve, reject) => {
        const countRequest = store.count();
        countRequest.onsuccess = () => {
          const count = countRequest.result;
          setPendingCount(count);
          resolve(count);
        };
        countRequest.onerror = () => reject(countRequest.error);
      });
    } catch (error) {
      console.error('Erro ao contar operações pendentes:', error);
      return 0;
    }
  }, [initDB]);

  // Sincronizar dados offline
  const syncOfflineData = useCallback(async () => {
    if (!isOnline || isSyncing) return;
    
    setIsSyncing(true);
    
    try {
      const db = await initDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const records = await new Promise<OfflineRecord[]>((resolve, reject) => {
        const getAllRequest = store.getAll();
        getAllRequest.onsuccess = () => resolve(getAllRequest.result);
        getAllRequest.onerror = () => reject(getAllRequest.error);
      });
      
      if (records.length === 0) {
        setIsSyncing(false);
        return;
      }
      
      let syncedCount = 0;
      const errors: string[] = [];
      
      for (const record of records) {
        try {
          await syncSingleRecord(record);
          const deleteRequest = store.delete(record.id);
          await new Promise<void>((resolve, reject) => {
            deleteRequest.onsuccess = () => resolve();
            deleteRequest.onerror = () => reject(deleteRequest.error);
          });
          syncedCount++;
        } catch (error) {
          console.error(`Erro ao sincronizar registro ${record.id}:`, error);
          errors.push(`${record.table}: ${error}`);
        }
      }
      
      await getPendingCount();
      
      if (syncedCount > 0) {
        toast({
          title: "Sincronização concluída",
          description: `${syncedCount} operação(s) sincronizada(s) com sucesso`,
        });
      }
      
      if (errors.length > 0) {
        toast({
          title: "Alguns erros na sincronização",
          description: `${errors.length} operação(s) com erro`,
          variant: "destructive",
        });
      }
      
    } catch (error) {
      console.error('Erro na sincronização:', error);
      toast({
        title: "Erro na sincronização",
        description: "Não foi possível sincronizar todos os dados",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, initDB]);

  // Sincronizar um registro específico
  const syncSingleRecord = async (record: OfflineRecord) => {
    const { table, action, data } = record;
    
    // Por enquanto, vamos logar os dados offline para implementação futura
    console.log('Sincronizando registro offline:', { table, action, data });
    
    // Implementação simplificada - salvar em uma tabela de sincronização
    try {
      const { error } = await supabase.from('audit_logs').insert({
        action: `offline_sync_${action}`,
        table_name: table,
        new_values: data,
        record_id: data.id || 'unknown'
      });
      
      if (error) {
        console.error('Erro ao sincronizar:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erro na sincronização offline:', error);
      throw error;
    }
  };

  // Limpar dados offline antigos (mais de 7 dias)
  const cleanupOldOfflineData = useCallback(async () => {
    try {
      const db = await initDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');
      
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const request = index.openCursor(IDBKeyRange.upperBound(sevenDaysAgo));
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      
    } catch (error) {
      console.error('Erro ao limpar dados offline antigos:', error);
    }
  }, [initDB]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Sincronizar automaticamente quando ficar online
      setTimeout(syncOfflineData, 1000);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast({
        title: "Modo offline",
        description: "Operações serão salvas localmente",
        duration: 5000,
      });
    };

    // Ouvir evento customizado para salvar operações offline
    const handleSaveOfflineOperation = (event: CustomEvent) => {
      const { table, action, data, userId } = event.detail;
      saveOfflineOperation(table, action, data, userId);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('saveOfflineOperation', handleSaveOfflineOperation as EventListener);
    
    // Carregar contagem inicial
    getPendingCount();
    
    // Limpeza automática de dados antigos
    cleanupOldOfflineData();
    
    // Sincronizar se estiver online
    if (isOnline) {
      syncOfflineData();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('saveOfflineOperation', handleSaveOfflineOperation as EventListener);
    };
  }, [syncOfflineData, getPendingCount, cleanupOldOfflineData, isOnline, saveOfflineOperation]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    saveOfflineOperation,
    syncOfflineData,
    getPendingCount
  };
};